// F-U / F-W batch actions for the Exhibits and Zones lists. Loops the
// existing per-item endpoints (no batch endpoint, decision 1), so each item
// keeps its own scope check, audit row and error envelope. A small worker
// pool keeps 200 calls quick without flooding the API; the list is
// invalidated ONCE at the end instead of once per item.

import { useQueryClient } from "@tanstack/react-query";
import { generateExhibitQR, transitionExhibit } from "../exhibits";
import { generateZoneQR, transitionZone } from "../zones";
import { exhibitKeys } from "./useExhibits";
import { zoneKeys } from "./useZones";

export type ContentKind = "exhibit" | "zone";
export type ContentBatchAction = "publish" | "unpublish" | "generate-qr";

export interface ContentBatchFailure {
  id: string;
  message: string;
}

export interface ContentBatchResult {
  succeeded: string[];
  failed: ContentBatchFailure[];
}

const BATCH_CONCURRENCY = 4;

const batchErrorMessage = (err: unknown): string =>
  err instanceof Error && err.message ? err.message : "Request failed";

async function runOne(
  kind: ContentKind,
  action: ContentBatchAction,
  id: string,
): Promise<void> {
  if (action === "generate-qr") {
    if (kind === "exhibit") await generateExhibitQR(id);
    else await generateZoneQR(id);
    return;
  }
  const to_state = action === "publish" ? "published" : "draft";
  if (kind === "exhibit") await transitionExhibit(id, { to_state });
  else await transitionZone(id, { to_state });
}

export function useContentBatch() {
  const qc = useQueryClient();
  return async (
    kind: ContentKind,
    action: ContentBatchAction,
    ids: string[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<ContentBatchResult> => {
    const result: ContentBatchResult = { succeeded: [], failed: [] };
    const queue = [...ids];
    let done = 0;
    const worker = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        try {
          await runOne(kind, action, id);
          result.succeeded.push(id);
        } catch (err) {
          result.failed.push({ id, message: batchErrorMessage(err) });
        }
        done += 1;
        onProgress?.(done, ids.length);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(BATCH_CONCURRENCY, ids.length) }, worker),
    );
    await qc.invalidateQueries({
      queryKey: kind === "exhibit" ? exhibitKeys.all : zoneKeys.all,
    });
    return result;
  };
}
